import type { ShopSchedule } from '@/api/types/shop.types';

const BAKU_TZ = 'Asia/Baku';

export interface TodayScheduleStatus {
  isOpen: boolean;
  label: string;
  statusLabel: string;
  timeLabel: string;
  nextTime?: string;
}

export interface ScheduleDayItem {
  dayName: string;
  hours: string;
}

function getNowInBaku(): {
  dayOfWeek: string;
  minutesSinceMidnight: number;
} {
  const now = new Date();
  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: BAKU_TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    weekday: 'long',
  });
  const parts = formatter.formatToParts(now);
  let hour = 0;
  let minute = 0;
  let weekday = '';
  for (const p of parts) {
    if (p.type === 'hour') hour = parseInt(p.value, 10) || 0;
    if (p.type === 'minute') minute = parseInt(p.value, 10) || 0;
    if (p.type === 'weekday') weekday = p.value;
  }
  return {
    dayOfWeek: weekday,
    minutesSinceMidnight: hour * 60 + minute,
  };
}

function parseTimeParts(timeStr: string): [number, number] {
  const normalized = (timeStr || '').trim().replace(/-/g, ':');
  const [h, m] = normalized
    .split(':')
    .map(s => parseInt(s?.trim() ?? '0', 10) || 0);
  return [h, m];
}

function parseTimeToMinutes(timeStr: string): number {
  const [h, m] = parseTimeParts(timeStr);
  return h * 60 + m;
}

function formatTime(timeStr: string): string {
  const [h, m] = parseTimeParts(timeStr);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function isSchedule24x7(entry: ShopSchedule): boolean {
  if (!entry?.openTime?.trim() || !entry?.closeTime?.trim()) return false;
  const openMinutes = parseTimeToMinutes(entry.openTime);
  const closeMinutes = parseTimeToMinutes(entry.closeTime);
  const dayEnd = 24 * 60;
  return openMinutes === 0 && (closeMinutes === 0 || closeMinutes >= dayEnd);
}

function getFirstClosedDayAfter(
  schedule: ShopSchedule[],
  afterIndex: number
): ShopSchedule | undefined {
  for (let step = 1; step <= 7; step++) {
    const index = (afterIndex + step) % 7;
    const entry = schedule[index] as ShopSchedule | undefined;
    const isClosed =
      !entry?.checked || !entry.openTime?.trim() || !entry.closeTime?.trim();
    if (isClosed) return entry;
  }
  return undefined;
}

function getNextOpeningFromSchedule(
  schedule: ShopSchedule[],
  afterIndex: number
): { time: string; dayOfWeek: string; is24_7: boolean } | null {
  for (let step = 1; step <= 7; step++) {
    const index = (afterIndex + step) % 7;
    const entry = schedule[index] as ShopSchedule | undefined;
    if (entry?.checked && entry.openTime?.trim()) {
      return {
        time: formatTime(entry.openTime),
        dayOfWeek: entry.dayOfWeek,
        is24_7: isSchedule24x7(entry),
      };
    }
  }
  return null;
}

type ScheduleT = (key: string, options?: Record<string, string>) => string;

function buildStatus(
  t: ScheduleT,
  isOpen: boolean,
  opts: { timeLabel?: string; nextTime?: string }
): TodayScheduleStatus {
  const statusLabel = t(isOpen ? 'shop.schedule_open' : 'shop.schedule_closed');
  const timeLabel = opts.timeLabel ?? '';
  return {
    isOpen,
    label: timeLabel ? `${statusLabel} ${timeLabel}` : statusLabel,
    statusLabel,
    timeLabel,
    nextTime: opts.nextTime,
  };
}

function getShortDayLabel(dayOfWeek: string, t: ScheduleT): string {
  return t(`shop.schedule_short_${dayOfWeek.toLowerCase()}`);
}

function isEntryOpenToday(
  entry: ShopSchedule,
  minutesSinceMidnight: number
): boolean {
  if (isSchedule24x7(entry)) return true;
  const openMinutes = parseTimeToMinutes(entry.openTime);
  const closeMinutes = parseTimeToMinutes(entry.closeTime);
  const endOfDay = 23 * 60 + 59;
  const isOpenUntilEndOfDay = closeMinutes >= endOfDay;
  return (
    minutesSinceMidnight >= openMinutes &&
    (isOpenUntilEndOfDay
      ? minutesSinceMidnight <= endOfDay
      : minutesSinceMidnight < closeMinutes)
  );
}

export function getTodayScheduleStatusFromSchedule(
  schedule: ShopSchedule[],
  t: ScheduleT
): TodayScheduleStatus | null {
  if (!schedule?.length) return null;

  const allWeek24x7 = isScheduleAll24x7(schedule);

  const { dayOfWeek, minutesSinceMidnight } = getNowInBaku();
  const todayIndex = schedule.findIndex(
    s => s.dayOfWeek.toLowerCase() === dayOfWeek.toLowerCase()
  );
  const entry = todayIndex >= 0 ? schedule[todayIndex] : undefined;
  const baseIndex = todayIndex >= 0 ? todayIndex : 0;

  const noValidEntryToday =
    !entry ||
    !entry.checked ||
    !entry.openTime?.trim() ||
    !entry.closeTime?.trim();

  if (noValidEntryToday) {
    const nextOpen = getNextOpeningFromSchedule(schedule, baseIndex);
    if (!nextOpen) return buildStatus(t, false, {});
    if (nextOpen.is24_7 && allWeek24x7) {
      return buildStatus(t, false, {
        timeLabel: t('shop.schedule_until', {
          time: t('shop.schedule_24_7'),
        }),
        nextTime: nextOpen.time,
      });
    }
    return buildStatus(t, false, {
      timeLabel: t('shop.schedule_until_day', {
        time: nextOpen.time,
        day: getShortDayLabel(nextOpen.dayOfWeek, t),
      }),
      nextTime: nextOpen.time,
    });
  }

  const isOpen = isEntryOpenToday(entry, minutesSinceMidnight);
  const openMinutes = parseTimeToMinutes(entry.openTime);
  const today24x7 = isSchedule24x7(entry);
  const timeLabel24x7 = t('shop.schedule_24_7');

  if (isOpen) {
    let timeLabel: string;
    if (today24x7 && allWeek24x7) {
      timeLabel = timeLabel24x7;
    } else {
      const closeTime = formatTime(entry.closeTime);
      const closeMinutes = parseTimeToMinutes(entry.closeTime);
      const closesAtMidnight = closeMinutes === 0 || closeMinutes >= 24 * 60;
      const firstClosedDay = getFirstClosedDayAfter(schedule, todayIndex);
      const closedDayShort =
        closesAtMidnight && firstClosedDay
          ? getShortDayLabel(firstClosedDay.dayOfWeek, t)
          : '';
      timeLabel = closedDayShort
        ? t('shop.schedule_until_day', { time: closeTime, day: closedDayShort })
        : t('shop.schedule_until', { time: closeTime });
    }
    return buildStatus(t, true, {
      timeLabel,
      nextTime:
        today24x7 && allWeek24x7 ? undefined : formatTime(entry.closeTime),
    });
  }

  if (minutesSinceMidnight < openMinutes) {
    let timeLabel: string;
    if (today24x7 && allWeek24x7) {
      timeLabel = timeLabel24x7;
    } else {
      const openTime = formatTime(entry.openTime);
      timeLabel = t('shop.schedule_until', { time: openTime });
    }
    return buildStatus(t, false, {
      timeLabel,
      nextTime:
        today24x7 && allWeek24x7 ? undefined : formatTime(entry.openTime),
    });
  }

  const nextOpen = getNextOpeningFromSchedule(schedule, todayIndex);
  if (!nextOpen) return buildStatus(t, false, {});

  if (nextOpen.is24_7 && allWeek24x7) {
    return buildStatus(t, false, {
      timeLabel: t('shop.schedule_until', {
        time: t('shop.schedule_24_7'),
      }),
      nextTime: nextOpen.time,
    });
  }
  return buildStatus(t, false, {
    timeLabel: t('shop.schedule_until_day', {
      time: nextOpen.time,
      day: getShortDayLabel(nextOpen.dayOfWeek, t),
    }),
    nextTime: nextOpen.time,
  });
}

export function isScheduleAll24x7(schedule: ShopSchedule[]): boolean {
  if (!schedule?.length || schedule.length < 7) return false;
  return schedule.every(
    e =>
      e.checked &&
      e.openTime?.trim() &&
      e.closeTime?.trim() &&
      isSchedule24x7(e)
  );
}

export function getScheduleDaysListFromSchedule(
  schedule: ShopSchedule[],
  t: (key: string) => string
): ScheduleDayItem[] {
  if (!schedule?.length) return [];

  const closedLabel = t('shop.schedule_closed');

  return schedule.map((entry: ShopSchedule) => {
    const dayName = t(`shop.schedule_${entry.dayOfWeek.toLowerCase()}`);
    const hours =
      entry.checked && entry.openTime?.trim() && entry.closeTime?.trim()
        ? `${formatTime(entry.openTime)} – ${formatTime(entry.closeTime)}`
        : closedLabel;
    return { dayName, hours };
  });
}
